import { motion } from 'framer-motion';
import { useMemo } from 'react';

// Big, slow-drifting blurred colour blobs that give the backdrop life. Colours
// come from the app theme: primary teal, secondary blue, accent lime.
const BLOBS = [
  { className: 'bg-primary/35', size: 620, left: '-12%', top: '-8%', dur: 19 },
  { className: 'bg-secondary/30', size: 540, left: '58%', top: '4%', dur: 23 },
  { className: 'bg-accent/18', size: 460, left: '68%', top: '55%', dur: 31 },
  { className: 'bg-primary/25', size: 560, left: '18%', top: '52%', dur: 27 },
];

interface CounterAmbientProps {
  intense?: boolean;
}

/**
 * Ambient animated background: drifting gradient blobs + slowly floating
 * particles. Particle layout is memoised so the 4s data poll doesn't reshuffle
 * them. `intense` bumps motion/particle count for the fullscreen present mode.
 */
export function CounterAmbient({ intense }: CounterAmbientProps) {
  const particles = useMemo(
    () =>
      Array.from({ length: intense ? 46 : 28 }).map(() => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 2 + Math.random() * 4,
        drift: 24 + Math.random() * 40,
        delay: Math.random() * 8,
        dur: 9 + Math.random() * 12,
      })),
    [intense],
  );

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {BLOBS.map((b) => (
        <motion.div
          key={`${b.left}-${b.top}`}
          className={`absolute rounded-full blur-3xl ${b.className}`}
          style={{
            width: b.size,
            height: b.size,
            left: b.left,
            top: b.top,
          }}
          animate={{
            x: [0, 90, -50, 0],
            y: [0, -70, 50, 0],
            scale: intense ? [1, 1.22, 1] : [1, 1.1, 1],
          }}
          transition={{
            duration: b.dur,
            repeat: Number.POSITIVE_INFINITY,
            ease: 'easeInOut',
          }}
        />
      ))}

      {particles.map((p, i) => (
        <motion.span
          // biome-ignore lint/suspicious/noArrayIndexKey: static memoised set
          key={i}
          className="absolute rounded-full bg-white/40"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{
            y: [0, -p.drift, 0],
            opacity: [0, 0.7, 0],
            scale: [0.6, 1, 0.6],
          }}
          transition={{
            duration: p.dur,
            delay: p.delay,
            repeat: Number.POSITIVE_INFINITY,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Vignette so the centre text stays legible over the blobs. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 45%, transparent 30%, rgba(0,0,0,0.55) 100%)',
        }}
      />
    </div>
  );
}
